/**
 * The vehicle watchlist, as a right-side panel over the Vehicle Crossing report.
 *
 * ─── WHY A DRAWER, AND WHY HERE ───
 * It is the same shape as the face report's Registered Faces panel, and for the same reason:
 * both are an operator-maintained registry of identities that changes what an AI event MEANS,
 * both are edited in response to what the report behind them shows, and both are consulted far
 * less often than the table they sit over. Overlaying rather than displacing keeps the
 * crossings — and the filters and scroll position the operator arrived with — untouched
 * underneath. Two registries that behave differently would be two things to learn.
 *
 * `onPlateSeed` closes the loop the other way: the report's plate cells hand a plate straight
 * into this form, which is the same gesture as registering a face from an unknown-face event.
 *
 * ─── WHAT A ROW MEANS ───
 * A listed plate raises an alert on EVERY crossing that reads it, on any camera, whether or not
 * the site has a vehicle alert rule — see the command-center's alertEvaluator. That is a loud
 * thing to switch on by typing in a box, so the panel is explicit about it: severity is shown
 * per entry, an expiry is offered for a temporary ban, and de-listing is a toggle that keeps
 * the record rather than a delete that loses who listed it and why.
 *
 * ─── PLATES ───
 * The node normalises what it is given (uppercase, separators stripped) and matches on that, so
 * "mh12 ab 1234" and "MH12AB1234" are one entry. What the operator typed is kept for display,
 * because that is the form they will scan the list for.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  PlusIcon, MagnifyingGlassIcon, TrashIcon, PencilSquareIcon,
  XMarkIcon, NoSymbolIcon, TruckIcon,
} from '@heroicons/react/24/outline';
import { useApi } from '../../api/ApiProvider';
import type { VehicleBlacklistEntry, VehicleBlacklistInput } from '../../api/contracts';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  DEFAULT_PAGE_SIZE,
  EmptyRows,
  PAGE_SIZE_OPTIONS,
  ResultsFooter,
  SkeletonRows,
  rowAction,
} from '../common/reportSurface';
import {
  FilterChips, FilterColumn, FilterSearch, FilterSection, PageWithFilters, useFilterColumn,
} from '../layout/FilterColumn';
import FillHeightCard, { TABLE_HEAD, TABLE_ROW, TABLE_TH, tableHeadRow } from '../layout/FillHeightCard';
import Modal from '../common/Modal';
import { BTN_PRIMARY, BTN_PRIMARY_SM, BTN_SECONDARY, BTN_ICON, SEARCH_INPUT } from '../common/ui';
import {
  Field, WATCHLIST_INPUT, WATCHLIST_SOLID_BTN, watchlistSeverityOf, WatchlistSeverityPicker,
  VEHICLE_WATCHLIST_CATEGORIES, WatchlistCategoryPicker, watchlistCategoryOf,
  DEFAULT_WATCHLIST_CATEGORY,
} from '../common/watchlistForm';

/** The same normalisation the node applies, so the form can preview what will be matched. */
export function canonicalPlate(raw: string): string {
  return String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * The severity ladder is SHARED with the person watchlist — see `common/watchlistForm`. Two
 * copies would let "Critical" come to mean one thing here and another on the face report.
 *
 * The CONTROL is shared now too. This form rendered the ladder as a `<select>` while the
 * person one rendered it as a segmented row, so the same three rungs looked like two different
 * settings depending on which watchlist you were editing — and a closed dropdown hides two
 * thirds of a ladder whose whole meaning is the comparison between its rungs.
 */
const severityOf = watchlistSeverityOf;

/**
 * WHAT the vehicle is listed as — stolen or blacklisted — as against how loudly to shout
 * about it. It names the alert ("Stolen Vehicle" rather than "Blacklisted Vehicle"), which is
 * the whole point: the response to a stolen car is a call to the police whoever is driving it.
 *
 * `category` has existed on the row since migration 052 but as FREE TEXT with a datalist of
 * whatever anybody had typed before — so it could not name anything, and two operators spelled
 * the same idea two ways. Existing values are still rendered, and editing an entry that holds
 * one says so rather than silently overwriting it. See `watchlistForm`.
 */
const CATEGORIES = VEHICLE_WATCHLIST_CATEGORIES;
const categoryOf = (v?: string | null) => watchlistCategoryOf(CATEGORIES, v);

const EMPTY_FORM: VehicleBlacklistInput = {
  plate_number: '', reason: '', category: DEFAULT_WATCHLIST_CATEGORY, notes: '',
  severity: 'critical', expires_at: '',
};

/** An entry whose expiry has passed is still a row, but it no longer matches anything. */
function isExpired(entry: VehicleBlacklistEntry): boolean {
  return !!entry.expires_at && new Date(entry.expires_at).getTime() <= Date.now();
}

function formatDay(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/** `<input type="datetime-local">` wants `YYYY-MM-DDTHH:mm` in LOCAL time, not an ISO string. */
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const INPUT = WATCHLIST_INPUT;

/* The inline table, as the Alert List draws it: one white card filling the rest of the screen,
   the header sticky inside it, and — with no strip above it inside the card — a white head row. */
const TABLE = 'w-full text-[13px]';
const TH = TABLE_TH;
const TD = 'px-3 py-2.5 align-middle text-foreground';
const ROW_CLICKABLE = `${TABLE_ROW} cursor-pointer`;
const ADD_BUTTON = BTN_PRIMARY_SM;

export interface VehicleBlacklistPanelProps {
  /**
   * A plate to pre-fill the register form with, handed over from a crossing the operator is
   * looking at. Changing it opens the form — that IS the gesture ("blacklist this vehicle"),
   * so requiring a second click to open the editor would be a step for nothing.
   */
  seedPlate?: string | null;
  /** Cleared once consumed, so re-seeding the same plate re-opens the form. */
  onSeedConsumed?: () => void;
  /** Told after any change, so the report can refetch rows whose verdict may now differ. */
  onChanged?: () => void;
  /** False hides every mutating control — the panel becomes a read-only list. */
  canEdit?: boolean;
  /** Dismiss the panel. */
  onClose?: () => void;
  /**
   * Where this is mounted, which decides its box — NOT its contents.
   *
   * `drawer` (the default) is the original: fixed to the right edge, over whatever the
   * operator was reading, because it is opened FROM a crossing and has to hand them back to
   * it. `inline` is the same panel as ordinary page content, for the Watch List page, where
   * it is not an interruption but the thing the operator came to see.
   *
   * A prop rather than a second component: the list, the form, the categories and the
   * expiry rules are the whole substance here, and a copy of them that drifted would mean
   * two different answers to "what does listing a plate do".
   */
  layout?: 'drawer' | 'inline';
  /**
   * Inline only: the page's toolbar strip (the Watch List's tabs), handed the panel's own
   * actions ("Add vehicle") to draw beside them. The inline panel is then the page body — a
   * flush filter column and one table card — so it must be rendered straight under <main>.
   */
  renderToolbar?: (actions: ReactNode) => ReactNode;
  /** Inline only: extra classes on the page body. */
  className?: string;
}

export default function VehicleBlacklistPanel({
  seedPlate = null,
  onSeedConsumed,
  onChanged,
  canEdit = true,
  onClose,
  layout = 'drawer',
  renderToolbar,
  className = '',
}: VehicleBlacklistPanelProps) {
  const { vehicleBlacklist } = useApi();

  const [entries, setEntries] = useState<VehicleBlacklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VehicleBlacklistInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<VehicleBlacklistEntry | null>(null);

  // Inline only — the drawer is one scrolling column and pages nothing. Client-side, because
  // the whole list is already here: a watchlist is a few hundred plates at the very most.
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  // The Watch List's column — same key as its People and Detections tabs, so folding it on one
  // tab folds it on all three. Read even by the drawer (hooks are unconditional); it draws none.
  const filterCol = useFilterColumn('watch-list');

  /** The category the form currently holds — it tints the dialog and names the alert. */
  const formCategory = categoryOf(form.category);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The whole list in one read. A watchlist is a few hundred plates at the very most —
      // the server paginates, but paging a list this size would cost the operator a
      // client-side search that works across all of it.
      const res = await vehicleBlacklist.list({ limit: 200 });
      setEntries(Array.isArray(res?.data) ? res.data : []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Could not load the vehicle watchlist');
    } finally {
      setLoading(false);
    }
  }, [vehicleBlacklist]);

  useEffect(() => { void load(); }, [load]);

  // A plate handed over from a crossing opens the form with it filled in.
  useEffect(() => {
    if (!seedPlate || !canEdit) return;
    setEditingId(null);
    setForm({ ...EMPTY_FORM, plate_number: seedPlate });
    setFormError(null);
    setFormOpen(true);
    onSeedConsumed?.();
  }, [seedPlate, canEdit, onSeedConsumed]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const canonical = term ? canonicalPlate(term) : '';
    return entries.filter((e) => {
      if (category && (e.category || DEFAULT_WATCHLIST_CATEGORY) !== category) return false;
      if (!term) return true;
      return (canonical && e.plate_number.includes(canonical))
        || (e.plate_display || '').toLowerCase().includes(term)
        || (e.reason || '').toLowerCase().includes(term)
        || (e.category || '').toLowerCase().includes(term);
    });
  }, [category, entries, search]);

  /** The page the inline table draws. The drawer ignores it and shows `visible` whole. */
  const paged = useMemo(
    () => visible.slice((page - 1) * limit, page * limit),
    [limit, page, visible],
  );

  // Narrowing changes what page 1 means, and staying on page 3 of the old set shows an empty
  // table for a filter that has matches.
  useEffect(() => { setPage(1); }, [search, category, limit]);

  const activeCount = useMemo(
    () => entries.filter((e) => e.is_active && !isExpired(e)).length,
    [entries],
  );

  function openAdd() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(entry: VehicleBlacklistEntry) {
    setEditingId(entry.id);
    setForm({
      plate_number: entry.plate_display || entry.plate_number,
      reason: entry.reason || '',
      // An entry from before the category existed opens on the default rather than on nothing
      // selected: the picker would otherwise offer no answer at all for a row that IS a
      // blacklist entry, and saving would quietly write null back over it.
      category: entry.category || DEFAULT_WATCHLIST_CATEGORY,
      notes: entry.notes || '',
      severity: entry.severity || 'critical',
      expires_at: toLocalInput(entry.expires_at),
    });
    setFormError(null);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  }

  async function save() {
    const typed = String(form.plate_number || '').trim();
    const canonical = canonicalPlate(typed);
    // Checked here as well as on the node: a plate too short to identify a vehicle would be
    // matched by nothing, and the operator would have no way to tell that from a working entry.
    if (canonical.length < 3) {
      setFormError('A plate needs at least three letters or digits.');
      return;
    }

    // Registering a plate that is already listed is an UPSERT on the node, so it would silently
    // overwrite the existing entry's reason and severity. Say so first — the operator almost
    // always meant to edit the row they could not find.
    if (!editingId) {
      const clash = entries.find((e) => e.plate_number === canonical);
      if (clash) {
        setFormError(
          `${clash.plate_display || clash.plate_number} is already listed`
          + `${clash.reason ? ` (${clash.reason})` : ''}. Saving will replace that entry.`);
        // Not a hard stop: replacing an entry is a perfectly reasonable thing to want.
        // Pointing `editingId` at the existing row turns the second press into an edit of it
        // rather than a blind overwrite.
        setEditingId(clash.id);
        return;
      }
    }

    setSaving(true);
    try {
      const payload: VehicleBlacklistInput = {
        plate_number: canonical,
        plate_display: typed,
        reason: form.reason || null,
        category: form.category || DEFAULT_WATCHLIST_CATEGORY,
        notes: form.notes || null,
        severity: form.severity || 'critical',
        // datetime-local gives local wall-clock with no zone; Date resolves it in the
        // browser's zone, which is the one the operator typed it in.
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };

      if (editingId) await vehicleBlacklist.update(editingId, payload);
      else await vehicleBlacklist.add(payload);

      closeForm();
      await load();
      onChanged?.();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Could not save this entry');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(entry: VehicleBlacklistEntry) {
    try {
      await vehicleBlacklist.update(entry.id, { is_active: !entry.is_active });
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || 'Could not update this entry');
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      await vehicleBlacklist.remove(confirmDelete.id);
      setConfirmDelete(null);
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || 'Could not remove this entry');
    }
  }


  /**
   * The register/edit dialog and the delete confirmation.
   *
   * A function rather than JSX written twice, because BOTH layouts raise them: the drawer
   * opens them over itself and the inline table opens them over the page. Two copies would
   * be two places for the form's defaults to drift, and the form IS the meaning of listing a
   * vehicle — see the note on the dialog below.
   */
  function renderDialogs() {
    return (
      <>
        {/*
          ── Register / edit, as a dialog ──
          It used to be a card that unfolded ABOVE the list, inside this drawer. Six fields in a
          28rem column that was already scrolling meant the Register button and the plate it
          belonged to were regularly on different screens, and the list shifted under the
          operator every time the form opened or closed. It is also the same act as the face
          report's watchlist dialog — listing an identity — and the two should not be two
          different kinds of interaction.
          The dialog wears the category's colour, as that one does: red for barred, violet for
          stolen. The last thing read before listing a vehicle should not contradict the word
          above it.
        */}
        <Modal
          open={formOpen && canEdit}
          onClose={saving ? () => {} : closeForm}
          // `md`, not `sm`: two segmented rows of three do not fit a 28rem column without the
          // labels wrapping mid-word.
          size="md"
          ariaLabel={editingId ? 'Edit watchlist entry' : 'Register a blacklisted vehicle'}
          title={
            <span className="flex items-center gap-2">
              <TruckIcon className={`h-5 w-5 shrink-0 ${formCategory?.accent || 'text-red-500'}`} />
              {editingId ? 'Edit entry' : 'Register a blacklisted vehicle'}
            </span>
          }
          subtitle={editingId ? form.plate_number : undefined}
          footer={
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={closeForm}
                className={BTN_SECONDARY}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={save}
                className={`${WATCHLIST_SOLID_BTN} ${formCategory?.solid || 'bg-red-500 hover:bg-red-600'}`}
              >
                {saving && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Register'}
              </button>
            </div>
          }
        >
          {/* The Modal shell is padding-free by design — content owns its inset. */}
          <div className="space-y-4 px-5 py-4">
            {/* The drawer says this in its header, but the dialog covers the drawer — so the one
                consequence the operator most needs in front of them would otherwise be hidden
                behind the form that causes it. */}
            <p className={`rounded-lg px-3 py-2.5 text-[12px] leading-relaxed ${formCategory?.banner || 'bg-muted text-muted-foreground'}`}>
              A listed plate raises a <span className="font-semibold">
                {formCategory?.alertName || 'watchlist'}
              </span> alert on every crossing that reads it, on any camera — with or without a
              vehicle alert rule.
            </p>

            <Field
              label="Number plate"
              // Shown because the stored key is not what was typed, and an operator who cannot
              // see that will not understand why "MH-12" and "MH 12" are one entry.
              hint={canonicalPlate(form.plate_number)
                ? `Matched as ${canonicalPlate(form.plate_number)}`
                : 'Spacing and dashes are ignored when matching.'}
            >
              <input
                value={form.plate_number}
                onChange={(e) => setForm({ ...form, plate_number: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && !saving) void save(); }}
                placeholder="MH 12 AB 1234"
                autoFocus
                disabled={saving}
                className={`${INPUT} font-mono uppercase disabled:opacity-50`}
              />
            </Field>

            {/* FIRST after the plate, because it is what the alert will be CALLED and the
                response to a stolen car is not the response to a barred one. */}
            <Field label="Listed as" hint={formCategory?.hint}>
              <WatchlistCategoryPicker
                categories={CATEGORIES}
                value={form.category || ''}
                onChange={(category) => setForm({ ...form, category })}
                disabled={saving}
              />
            </Field>

            <Field label="Reason">
              <input
                value={form.reason || ''}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Case number, who asked, what happened"
                disabled={saving}
                className={`${INPUT} disabled:opacity-50`}
              />
            </Field>

            <Field label="Alert severity" hint={severityOf(form.severity)?.hint}>
              <WatchlistSeverityPicker
                value={form.severity || 'critical'}
                onChange={(severity) => setForm({ ...form, severity })}
                disabled={saving}
              />
            </Field>

            <Field label="Expires (optional)" hint="Leave empty to list the vehicle indefinitely.">
              <input
                type="datetime-local"
                value={form.expires_at || ''}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                disabled={saving}
                className={`${INPUT} disabled:opacity-50`}
              />
            </Field>

            <Field label="Notes">
              <input
                value={form.notes || ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything the next operator needs to know"
                disabled={saving}
                className={`${INPUT} disabled:opacity-50`}
              />
            </Field>

            {/* Amber, not red: the commonest one is "that plate is already listed", which is a
                thing to read before pressing again rather than a failure. */}
            {formError && (
              <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-[12px] leading-snug text-amber-500">
                {formError}
              </p>
            )}
          </div>
        </Modal>

        <ConfirmDialog
          open={!!confirmDelete}
          title="Remove this plate from the watchlist?"
          message={
            <>
              <span className="font-mono font-semibold text-foreground">
                {confirmDelete?.plate_display || confirmDelete?.plate_number}
              </span>{' '}
              will stop raising alerts. Crossings it already flagged keep their record — only the
              listing goes. To pause it instead and keep the reason, use the block toggle.
            </>
          }
          confirmLabel="Remove"
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      </>
    );
  }

  const inline = layout === 'inline';

  /** The three states a row can be in. "Expired" is an entry that looks listed and matches
   *  nothing — the failure an operator must be able to see, so it is never folded into "Off". */
  function statusOf(entry: VehicleBlacklistEntry) {
    const expired = isExpired(entry);
    const live = entry.is_active && !expired;
    return {
      live,
      expired,
      label: live ? 'Watching' : expired ? 'Expired' : 'Off',
      chip: live ? 'bg-emerald-500/15 text-emerald-500'
        : expired ? 'bg-amber-500/15 text-amber-500'
          : 'bg-muted text-muted-foreground',
    };
  }

  /**
   * INLINE — the reports' surface, so the Vehicles tab and the People tab beside it are the
   * same list with different columns. The drawer keeps the card list below: it is 28rem wide,
   * opened over a crossing, and a six-column table does not belong in it.
   */
  if (inline) {
    const addButton = canEdit ? (
      <button type="button" onClick={openAdd} className={ADD_BUTTON}>
        <PlusIcon className="h-4 w-4" /> Add vehicle
      </button>
    ) : null;
    return (
      <>
        <PageWithFilters
          className={className}
          filters={
            <FilterColumn
              width="wide"
              open={filterCol.open}
              onToggle={filterCol.toggle}
              label="Filters"
              activeCount={[!!search, !!category].filter(Boolean).length}
              onClearAll={() => { setSearch(''); setCategory(''); }}
            >
              <FilterSearch value={search} onChange={setSearch} placeholder="Plate, reason or category…" />

              <FilterSection title="Category">
                <FilterChips<string>
                  cols={2}
                  value={category}
                  onChange={setCategory}
                  options={[
                    { value: '', label: 'All' },
                    ...VEHICLE_WATCHLIST_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
                  ]}
                />
              </FilterSection>

              <FilterSection title="Rows per page">
                <FilterChips<number>
                  cols={4}
                  value={limit}
                  onChange={setLimit}
                  options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
                />
              </FilterSection>
            </FilterColumn>
          }
          toolbar={renderToolbar ? renderToolbar(addButton) : addButton && <div className="flex justify-end p-2">{addButton}</div>}
        >
        {error && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-[12px] text-red-500">{error}</p>
        )}

        {/* ── Results — one card filling the rest of the screen, header sticky inside it ── */}
        <FillHeightCard>
          <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <SkeletonRows />
          ) : !visible.length ? (
            <EmptyRows>
              <p>
                {entries.length === 0
                  ? 'No vehicles are on the watch list yet.'
                  : 'No vehicle on the watch list matches the selected filters.'}
              </p>
            </EmptyRows>
          ) : (
              <table className={TABLE}>
                <thead className={TABLE_HEAD}>
                  <tr className={tableHeadRow(false)}>
                    <th className={TH}>Plate</th>
                    <th className={TH}>Category</th>
                    <th className={TH}>Severity</th>
                    <th className={TH}>Watching</th>
                    <th className={TH}>Reason</th>
                    <th className={TH}>Listed</th>
                    {canEdit && <th className={TH} />}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((entry) => {
                    const st = statusOf(entry);
                    const sev = severityOf(entry.severity);
                    const cat = categoryOf(entry.category);
                    return (
                      <tr
                        key={entry.id}
                        onClick={canEdit ? () => openEdit(entry) : undefined}
                        className={canEdit ? ROW_CLICKABLE : TABLE_ROW}
                      >
                        <td className={TD}>
                          <span className="flex items-center gap-2.5">
                            <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg ${
                              st.live ? 'bg-red-500/15' : 'bg-muted'
                            }`}>
                              <TruckIcon className={`h-4 w-4 ${st.live ? 'text-red-500' : 'text-muted-foreground'}`} />
                            </span>
                            <span className={`font-mono font-semibold ${
                              st.live ? 'text-foreground' : 'text-muted-foreground line-through'
                            }`}>
                              {entry.plate_display || entry.plate_number}
                            </span>
                          </span>
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {entry.category ? (
                            <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${cat?.chip || 'border border-border text-muted-foreground'}`}>
                              {cat?.label || entry.category}
                            </span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${sev?.chip || 'bg-muted text-muted-foreground'}`}>
                            {sev?.label || entry.severity}
                          </span>
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          <span
                            title={st.expired ? `Expired ${formatDay(entry.expires_at)}` : undefined}
                            className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${st.chip}`}
                          >
                            {st.label}
                          </span>
                        </td>
                        <td className={`${TD} max-w-[22rem]`}>
                          <span className="block truncate text-muted-foreground" title={entry.reason || undefined}>
                            {entry.reason || '—'}
                          </span>
                        </td>
                        <td className={`${TD} whitespace-nowrap text-muted-foreground`}>
                          {formatDay(entry.created_at)}
                        </td>
                        {canEdit && (
                          <td className={`${TD} whitespace-nowrap text-right`}>
                            {/* The row opens the editor; these are the two acts the editor is
                                not — pausing without forgetting, and forgetting. Stopped from
                                opening it as well, or every click would do two things. */}
                            <span className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => toggleActive(entry)}
                                title={entry.is_active ? 'Stop watching (keeps the record)' : 'Resume watching'}
                                aria-label={entry.is_active ? 'Stop watching' : 'Resume watching'}
                                className={`${rowAction()} ${entry.is_active ? 'text-red-500' : ''}`}
                              >
                                <NoSymbolIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(entry)}
                                title="Remove from the list"
                                aria-label="Remove from the list"
                                className={rowAction('danger')}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          )}
          </div>

          {!loading && visible.length > 0 && (
            <ResultsFooter
              page={page}
              totalPages={Math.ceil(visible.length / limit)}
              total={visible.length}
              limit={limit}
              noun="vehicles"
              onPage={setPage}
            />
          )}
        </FillHeightCard>
        </PageWithFilters>

        {renderDialogs()}
      </>
    );
  }

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md border-l border-border bg-card shadow-2xl overflow-y-auto">
      {/* ── Head ── */}
      <div className="sticky top-0 z-10 border-b border-border bg-card px-5 py-4 backdrop-blur">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            Blacklisted Vehicles
            {activeCount > 0 && (
              <span className="rounded-md bg-red-500/15 px-2 py-0.5 text-[11px] font-bold text-red-500">
                {activeCount} active
              </span>
            )}
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              title="Close"
              className={BTN_ICON}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Said once, at the top, because it is the consequence of everything below it. */}
        <p className="mt-1 text-[11px] text-muted-foreground">
          A listed plate alerts on every crossing that reads it, on any camera — with or without
          a vehicle alert rule.
        </p>

        {/* No longer hidden while the form is open: the form is a dialog over this panel now,
            so hiding the button behind it only made the header jump. */}
        {canEdit && (
          <button
            onClick={openAdd}
            className={`${BTN_PRIMARY} mt-3 w-full`}
          >
            <PlusIcon className="h-4 w-4" /> Register Blacklist Vehicle
          </button>
        )}

        <div className="relative mt-3">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plate, reason or category…"
            className={SEARCH_INPUT}
          />
        </div>
      </div>

      <div className="p-5">
        {error && (
          <p className="mb-3 rounded-lg bg-red-500/10 px-3 py-2 text-[12px] text-red-500">{error}</p>
        )}

        {/* ── List ── */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />)}
          </div>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {entries.length === 0
              ? 'No vehicles are blacklisted yet. Register one above, or from a crossing in the report.'
              : 'No entry matches that search.'}
          </p>
        ) : (
          <div className="space-y-3">
            {visible.map((entry) => {
              const expired = isExpired(entry);
              const live = entry.is_active && !expired;
              const sev = severityOf(entry.severity);
              return (
                <div key={entry.id} className="rounded-xl border border-border bg-background hover:bg-muted transition-colors">
                  <div className="flex items-start gap-3 p-3">
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${
                      live ? 'bg-red-500/15' : 'bg-muted'
                    }`}>
                      <TruckIcon className={`h-6 w-6 ${live ? 'text-red-500' : 'text-muted-foreground'}`} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className={`truncate font-mono text-[13px] font-semibold ${
                        live ? 'text-foreground' : 'text-muted-foreground line-through'
                      }`}>
                        {entry.plate_display || entry.plate_number}
                      </p>

                      <div className="mt-0.5 flex flex-wrap items-center gap-2">
                        {/* Three states, not two. "Expired" is an entry that looks listed and
                            matches nothing — the failure an operator must be able to see. */}
                        <span className={`whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase ${
                          live ? 'bg-emerald-500/15 text-emerald-500'
                            : expired ? 'bg-amber-500/15 text-amber-500'
                              : 'bg-muted text-muted-foreground'
                        }`}
                          title={expired ? `Expired ${formatDay(entry.expires_at)}` : undefined}
                        >
                          {live ? 'Watching' : expired ? 'Expired' : 'Off'}
                        </span>
                        <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${sev?.chip || 'bg-muted text-muted-foreground'}`}>
                          {sev?.label || entry.severity}
                        </span>
                        {entry.category && (
                          <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${
                            categoryOf(entry.category)?.chip || 'bg-muted text-muted-foreground'
                          }`}>
                            {categoryOf(entry.category)?.label || entry.category}
                          </span>
                        )}
                      </div>

                      {entry.reason && (
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{entry.reason}</p>
                      )}
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        Added {formatDay(entry.created_at)}{entry.added_by ? ` by ${entry.added_by}` : ''}
                      </p>
                    </div>

                    {canEdit && (
                      <div className="flex flex-shrink-0 items-center">
                        <button
                          onClick={() => openEdit(entry)}
                          title="Edit"
                          aria-label="Edit"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleActive(entry)}
                          title={entry.is_active ? 'Stop watching (keeps the record)' : 'Resume watching'}
                          aria-label={entry.is_active ? 'Stop watching' : 'Resume watching'}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-muted ${
                            entry.is_active ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <NoSymbolIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(entry)}
                          title="Remove from the list"
                          aria-label="Remove from the list"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {renderDialogs()}
    </div>
  );
}

